import {
  BridgeError,
  call,
  isNativeHost,
  onBridgeEvent,
} from "@/bridge/bridge";
import { env, isDevBuild } from "@/config/env";

import {
  clearTokens,
  isFresh,
  readTokens,
  writeTokens,
  type IssuedTokens,
} from "./tokenStore";

const EXCHANGE_DEBUG_KEY = "sgc.debug.exchange";

export type ExchangeSource = "boot" | "renew" | "dev";

function recordExchangeAttempt(entry: Record<string, unknown>): void {
  if (!isDevBuild) return;
  try {
    const raw = window.localStorage.getItem(EXCHANGE_DEBUG_KEY);
    const trail: unknown[] = raw === null ? [] : JSON.parse(raw);
    trail.push({ at: new Date().toISOString(), ...entry });
    window.localStorage.setItem(
      EXCHANGE_DEBUG_KEY,
      JSON.stringify(trail.slice(-20)),
    );
  } catch {
    // Diagnostics must never break the flow they are diagnosing.
  }
}

/**
 * The web session is a pair of tokens held in localStorage (see tokenStore):
 * a short-lived access token sent as a bearer, and a refresh token that buys
 * the next pair.
 *
 * This module owns getting them and keeping them current. Three things can
 * produce a session, in the order they are tried:
 *
 *   1. the one-time code the native shell injected at boot;
 *   2. the stored refresh token, when that code has already been spent — which
 *      is what a WebView reload looks like, since the shell replays the same
 *      injected code every time the page loads;
 *   3. a fresh code asked of the shell over the bridge.
 */

export const WEBVIEW_SESSION_PATHS = {
  exchange: "/v1/webview/session/exchange",
  refresh: "/v1/webview/session/refresh",
  logout: "/v1/webview/session/logout",
} as const;

let inFlightExchange: { code: string; done: Promise<boolean> } | null = null;
let inFlightRefresh: Promise<boolean> | null = null;

interface SessionResponseBody {
  payload?: Partial<IssuedTokens> | null;
}

function readIssuedTokens(body: SessionResponseBody): IssuedTokens | null {
  const issued = body?.payload;
  if (!issued) return null;

  const { accessToken, refreshToken, expiresIn } = issued;
  if (!accessToken || !refreshToken) return null;
  if (typeof expiresIn !== "number" || !Number.isFinite(expiresIn)) return null;

  return { accessToken, expiresIn, refreshToken };
}

export function exchangeSession(
  code: string,
  source: ExchangeSource = "boot",
): Promise<boolean> {
  if (inFlightExchange !== null && inFlightExchange.code === code) {
    return inFlightExchange.done;
  }
  inFlightExchange = { code, done: postExchange(code, source) };
  return inFlightExchange.done;
}

async function postExchange(
  code: string,
  source: ExchangeSource,
): Promise<boolean> {
  const url = `${env.apiBaseUrl}${WEBVIEW_SESSION_PATHS.exchange}`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });

    const tokens = response.ok ? readIssuedTokens(await response.json()) : null;
    recordExchangeAttempt({
      source,
      url,
      status: response.status,
      ok: response.ok,
      tokens: tokens !== null,
    });

    if (tokens === null) return false;
    writeTokens(tokens);
    return true;
  } catch (error) {
    recordExchangeAttempt({
      source,
      url,
      threw: error instanceof Error ? error.message : String(error),
    });
    return false;
  } finally {
    inFlightExchange = null;
  }
}

export function refreshTokens(): Promise<boolean> {
  const stored = readTokens();
  if (stored === null) return Promise.resolve(false);

  inFlightRefresh ??= postRefresh(stored.refreshToken);
  return inFlightRefresh;
}

async function postRefresh(refreshToken: string): Promise<boolean> {
  try {
    const response = await fetch(
      `${env.apiBaseUrl}${WEBVIEW_SESSION_PATHS.refresh}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      },
    );

    const tokens = response.ok ? readIssuedTokens(await response.json()) : null;
    if (tokens === null) {
      if (readTokens()?.refreshToken === refreshToken) clearTokens();
      return false;
    }

    writeTokens(tokens);
    return true;
  } catch {
    return false;
  } finally {
    inFlightRefresh = null;
  }
}

export async function openSession(code: string | null): Promise<boolean> {
  if (code === null) {
    if (isNativeHost()) {
      await revokeTokens();
      return false;
    }
    // A plain browser has no shell to ask; /dev/login opens the session there.
    return true;
  }

  if (await exchangeSession(code)) {
    return true;
  }

  console.warn("[session] the injected code was not accepted; reauthorizing");
  return reauthorize();
}

let inFlightReauthorize: Promise<boolean> | null = null;

export function reauthorize(): Promise<boolean> {
  inFlightReauthorize ??= runReauthorize();
  return inFlightReauthorize;
}

async function runReauthorize(): Promise<boolean> {
  try {
    if (await refreshTokens()) return true;
    return await renewSession();
  } finally {
    inFlightReauthorize = null;
  }
}

export async function renewSession(): Promise<boolean> {
  if (!isNativeHost()) return false;

  try {
    const { code } = await call("session.exchangeCode");
    if (code === null) return false;
    return await exchangeSession(code, "renew");
  } catch (error) {
    if (error instanceof BridgeError) return false;
    throw error;
  }
}

export async function currentAccessToken(): Promise<string | null> {
  const stored = readTokens();
  if (stored === null) return null;
  if (isFresh(stored)) return stored.accessToken;

  if (!(await refreshTokens())) return null;
  return readTokens()?.accessToken ?? null;
}

export async function revokeTokens(): Promise<void> {
  const stored = readTokens();
  clearTokens();
  if (stored === null) return;

  try {
    await fetch(`${env.apiBaseUrl}${WEBVIEW_SESSION_PATHS.logout}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: stored.refreshToken }),
    });
  } catch {
    // Best effort. The session expires on its own anyway.
  }
}

export async function signOut(): Promise<void> {
  await revokeTokens();

  if (!isNativeHost()) return;

  try {
    await call("session.signOut");
  } catch (error) {
    if (!(error instanceof BridgeError)) throw error;
  }
}

export function watchNativeSignOut(): () => void {
  return onBridgeEvent("session.changed", (event) => {
    if (event.accessToken === null) void revokeTokens();
  });
}
